import React, { useRef } from 'react'
import { observer } from 'mobx-react'
import { Box, IconButton, LinearProgress, Typography, useTheme } from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import PauseIcon from '@mui/icons-material/Pause'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { Icons, getFileSizeFromBytes } from '../../../../utils/utilities'
import { useHybridClassVideoUploadStore } from '../../../../store/hybridclassvideoupload.store'

const THUMBNAIL_ACCEPT = "image/jpeg,image/png,image/webp"

const jobProgress = ( job ) => {
    if ( !job.files?.length ) return 0

    const { totalProgressWeight, totalSize } = job.files.reduce(
        ( acc, file ) => {
            const size = file.sizeBytes || 0
            const progress = file.progress || 0

            return {
                totalProgressWeight: acc.totalProgressWeight + ( size * progress ),
                totalSize: acc.totalSize + size,
            }
        },
        { totalProgressWeight: 0, totalSize: 0 }
    )

    if ( !totalSize ) {
        const sumProgress = job.files.reduce( ( sum, f ) => sum + ( f.progress || 0 ), 0 )
        return Math.round( sumProgress / job.files.length )
    }

    return Math.round( totalProgressWeight / totalSize )
}

const jobSizeBytes = ( job ) => job.files?.reduce( ( sum, f ) => sum + ( f.sizeBytes || 0 ), 0 ) || 0

const JobItem = observer( ( { job, border } ) => {
    const store = useHybridClassVideoUploadStore()
    const fileInputRef = useRef( null )

    const percent = jobProgress( job )
    const isSaved = job.status === "saved"
    const isSaving = job.status === "saving"
    const isUploading = job.status === "uploading"
    const isPaused = job.status === "paused"
    const isExpired = job.status === "expired"
    const isFailed = job.status === "failed" || isExpired

    // Set by the store after a reload when the browser no longer has the file's data.
    const missing = job.missingFiles?.[0]
    const needsFile = Boolean( job.needsFile && missing )

    const handleResumeClick = () => {
        if ( needsFile ) {
            // Must run directly inside the click handler, otherwise the browser blocks the picker.
            fileInputRef.current?.click()
            return
        }
        // Store waits for the restore; if the file turns out to be missing it sets
        // job.needsFile and this button switches to "Re-select file".
        store.resumeJob( job.id )
    }

    const handleFileChange = ( e ) => {
        const file = e.target.files?.[0]
        e.target.value = "" // allow picking the same file again after an error
        if ( !file || !missing ) return

        // Quick check before handing over; the store validates again (incl. lastModified).
        if ( file.name !== missing.name || file.size !== missing.size ) {
            store.patchJobMeta( job.id, { error: `Wrong file selected. Please choose "${missing.name}".` } )
            return
        }
        store.reattachAndResumeJob( job.id, file )
    }

    const statusText = isFailed
        ? ( job.error || "Upload failed" )
        : isPaused
            ? ( job.error || "Upload stopped" )
            : isSaved
                ? "Upload complete"
                : isSaving
                    ? "Finishing up..."
                    : `${percent}% · ${getFileSizeFromBytes( jobSizeBytes( job ) )}`

    const progressColor = isFailed ? "error" : isPaused ? "warning" : isSaved ? "success" : "secondary"

    return (
        <Box display="flex" gap="11px" alignItems="center" padding="12px 14px" borderBottom={border[1]}>
            {/* Hidden input to re-select the file after a page reload */}
            <input
                type="file"
                ref={fileInputRef}
                accept={missing?.role === "thumbnail" ? THUMBNAIL_ACCEPT : "video/*"}
                style={{ display: "none" }}
                onChange={handleFileChange}
            />

            <Box flexGrow={1} minWidth={0}>
                <Typography fontSize="12.5px" fontWeight="600" noWrap>{job.title}</Typography>
                <LinearProgress
                    variant={isSaving ? "indeterminate" : "determinate"}
                    value={isSaved ? 100 : percent}
                    color={progressColor}
                    sx={{ height: "4px", borderRadius: "3px", marginTop: "7px" }}
                />
                <Typography
                    fontSize="11px"
                    color={isFailed ? "error.main" : isPaused ? "warning.main" : "textSecondary"}
                    marginTop="5px"
                    noWrap
                    title={statusText}
                >
                    {statusText}
                </Typography>
            </Box>

            {isUploading && (
                <IconButton size="small" title="Stop upload" onClick={() => store.stopJob( job.id )}>
                    <PauseIcon fontSize="small" />
                </IconButton>
            )}

            {( isFailed || isPaused ) && (
                <>
                    {!isExpired && (
                        <IconButton
                            size="small"
                            title={needsFile ? `Re-select "${missing.name}" to resume` : "Resume upload"}
                            onClick={handleResumeClick}
                        >
                            {needsFile ? <UploadFileIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                        </IconButton>
                    )}
                    <IconButton size="small" title="Discard upload" onClick={() => store.discard( job.id )}>
                        {Icons.small.CloseIcon}
                    </IconButton>
                </>
            )}

            {isSaved && (
                <Box color="success.main" display="flex" fontSize="18px">
                    {Icons.default.CheckCircleIcon}
                </Box>
            )}
        </Box>
    )
} )

const UploadProgressTray = observer( () => {
    const store = useHybridClassVideoUploadStore()
    const { palette, border, layout } = useTheme()

    const jobs = store?.getJobs || []
    if ( jobs.length === 0 ) return null

    const remaining = jobs.filter( job => job.status === "uploading" || job.status === "saving" ).length
    const title = remaining > 0 ? `Uploading ${remaining} of ${jobs.length}` : `All ${jobs.length} upload${jobs.length > 1 ? "s" : ""} complete`

    return (
        <Box position="fixed" right="22px" bottom="22px" width="340px" bgcolor={palette.background.paper} borderRadius={layout.borderRadius} boxShadow={6} overflow="hidden" zIndex={70}>
            <Box display="flex" alignItems="center" gap="10px" padding="10px 8px 10px 16px" bgcolor="primary.main" color="primary.contrastText">
                <Typography fontSize="13px" fontWeight="600" flexGrow={1}>{title}</Typography>
                <IconButton size="small" onClick={() => store.toggleTray()} sx={{ color: "inherit" }}>
                    {store.trayMinimized ? Icons.small.KeyboardArrowUp : Icons.small.CloseIcon}
                </IconButton>
            </Box>
            {!store.trayMinimized && (
                <Box maxHeight="290px" overflow="auto">
                    {jobs.map( job => (
                        <JobItem key={job.id} job={job} border={border} />
                    ) )}
                </Box>
            )}
        </Box>
    )
} )

export default UploadProgressTray